"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  createInitialState,
  stepGame,
  togglePause,
  type Direction,
  type GameState,
  toKey,
} from "@/lib/snake";

const GRID_ROWS = 18;
const GRID_COLS = 18;
const TICK_MS = 140;

const DIRECTION_KEYS: Record<string, Direction> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  w: "up",
  a: "left",
  s: "down",
  d: "right",
};

export function SnakeGame() {
  const [state, setState] = useState<GameState>(() =>
    createInitialState({ rows: GRID_ROWS, cols: GRID_COLS }, Math.random),
  );
  const pendingDirection = useRef<Direction | null>(null);
  const rafRef = useRef<number | null>(null);

  const snakeCells = useMemo(() => {
    return new Set(state.snake.map((segment) => toKey(segment)));
  }, [state.snake]);

  const handleDirection = useCallback((direction: Direction) => {
    pendingDirection.current = direction;
  }, []);

  const handleRestart = useCallback(() => {
    pendingDirection.current = null;
    setState(createInitialState({ rows: GRID_ROWS, cols: GRID_COLS }, Math.random));
  }, []);

  const handleTogglePause = useCallback(() => {
    setState((prev) => togglePause(prev));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === " ") {
        event.preventDefault();
        handleTogglePause();
        return;
      }
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const direction = DIRECTION_KEYS[key];
      if (direction) {
        event.preventDefault();
        handleDirection(direction);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleDirection, handleTogglePause]);

  useEffect(() => {
    if (state.status !== "playing") {
      if (rafRef.current) {
        window.clearInterval(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    if (!rafRef.current) {
      const id = window.setInterval(() => {
        setState((prev) => {
          const next = stepGame(prev, pendingDirection.current, Math.random);
          pendingDirection.current = null;
          return next;
        });
      }, TICK_MS);
      rafRef.current = id;
    }

    return () => {
      if (rafRef.current) {
        window.clearInterval(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [state.status]);

  const statusLabel =
    state.status === "gameover"
      ? "Game Over"
      : state.status === "paused"
        ? "Paused"
        : "Playing";

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="rounded-[32px] border border-white/10 bg-surface-primary/95 p-6 text-white shadow-[0_30px_90px_-60px_rgba(4,18,43,0.9)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-secondary">
              Classic Snake
            </p>
            <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">
              Score {state.score}
            </h1>
            <p className="mt-1 text-sm text-white/70">{statusLabel}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={handleTogglePause}
              disabled={state.status === "gameover"}
            >
              {state.status === "paused" ? "Resume" : "Pause"}
            </Button>
            <Button size="sm" onClick={handleRestart}>
              Restart
            </Button>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-3xl border border-white/10 bg-surface-secondary p-3">
          <div
            className="grid aspect-square w-full gap-1"
            style={{
              gridTemplateColumns: `repeat(${state.cols}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: state.rows * state.cols }, (_, index) => {
              const x = index % state.cols;
              const y = Math.floor(index / state.cols);
              const key = `${x}:${y}`;
              const isSnake = snakeCells.has(key);
              const isFood = state.food ? toKey(state.food) === key : false;
              return (
                <div
                  key={key}
                  className={
                    "rounded-[6px] border border-white/5 bg-surface-tertiary transition-colors" +
                    (isSnake
                      ? " bg-gradient-to-br from-[#35A29F] via-[#088395] to-[#071952]"
                      : isFood
                        ? " bg-[#F2F7A1]"
                        : "")
                  }
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-[28px] border border-white/10 bg-white/95 p-6 text-foreground shadow-[0_30px_80px_-60px_rgba(7,25,82,0.35)]">
        <div>
          <h2 className="text-lg font-semibold">Controls</h2>
          <p className="mt-2 text-sm text-foreground/70">
            Gunakan tombol panah atau WASD. Tekan spasi untuk pause.
          </p>
        </div>
        <div className="grid gap-3">
          <div className="grid grid-cols-3 gap-2">
            <div />
            <Button
              variant="outline"
              size="icon"
              className="border-foreground/20"
              onClick={() => handleDirection("up")}
            >
              ↑
            </Button>
            <div />
            <Button
              variant="outline"
              size="icon"
              className="border-foreground/20"
              onClick={() => handleDirection("left")}
            >
              ←
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="border-foreground/20"
              onClick={() => handleDirection("down")}
            >
              ↓
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="border-foreground/20"
              onClick={() => handleDirection("right")}
            >
              →
            </Button>
          </div>
          <Button variant="ghost" className="justify-start" onClick={handleRestart}>
            Mulai ulang
          </Button>
        </div>
        <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.03] p-4 text-sm text-foreground/70">
          Tabrakan dengan dinding atau badan sendiri akan mengakhiri permainan.
        </div>
      </div>
    </div>
  );
}
